import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useResponsive } from '../../../hooks/useResponsive';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { CategoryBreakdown } from '../types';

interface StatisticsCardsProps {
  statistics: CategoryBreakdown[];
}

const BAR_CHART_HEIGHT = 160;

function BarChart({ segments, isRTL }: { segments: CategoryBreakdown['segments']; isRTL: boolean }) {
  const maxVal = Math.max(...segments.map((s) => s.value));
  const yMax = Math.max(10, Math.ceil(maxVal / 5) * 5);
  const yLabels: number[] = [];
  for (let i = yMax; i >= 0; i -= 5) yLabels.push(i);

  return (
    <View style={barStyles.container}>
      {/* Y-axis */}
      <View style={barStyles.yColumn}>
        <View style={barStyles.yChartPart}>
          {yLabels.map((val) => (
            <Text
              key={val}
              style={[
                barStyles.yLabel,
                { bottom: `${(val / yMax) * 100}%` },
              ]}
            >
              {val}
            </Text>
          ))}
        </View>
        <View style={barStyles.yLabelSpacer} />
      </View>

      {/* Bars + labels area */}
      <View style={barStyles.barsArea}>
        {/* Chart area with bottom and side borders */}
        <View style={barStyles.chartBorder}>
          {/* Grid lines */}
          {yLabels.map((val, idx) => (
            <View
              key={val}
              style={[
                barStyles.gridLine,
                {
                  bottom: `${(val / yMax) * 100}%`,
                },
              ]}
            />
          ))}
        </View>

        {/* Bar columns */}
        <View style={[barStyles.columnsRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          {segments.map((seg, i) => {
            const heightPct = yMax > 0 ? (seg.value / yMax) * 100 : 0;
            return (
              <View key={i} style={barStyles.column}>
                <View style={barStyles.barTrack}>
                  <View style={[barStyles.bar, { backgroundColor: seg.color, height: `${heightPct}%` }]} />
                </View>
                <Text style={barStyles.barLabel}>
                  {seg.label}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
}

export default function StatisticsCards({ statistics }: StatisticsCardsProps) {
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();

  return (
    <View
      style={[
        styles.row,
        isMobile && styles.rowMobile,
        { flexDirection: isRTL ? 'row-reverse' : 'row' },
        isMobile && { flexDirection: 'column' },
      ]}
    >
      {statistics.map((stat, index) => (
        <View
          key={index}
          style={[
            styles.card,
            {
              backgroundColor: isDark ? colors.card : '#FFFFFF',
              borderColor: isDark ? colors.cardBorder : '#e2e8f0',
            },
            createShadow(0, 1, 2, '#000', 0.05),
            isMobile && styles.cardMobile,
          ]}
        >
          {/* Title */}
          <Text style={[styles.cardTitle, { color: '#414042' }]}>
            {stat.title}
          </Text>

          {/* Content: bar chart + optional total box */}
          <View
            style={[
              styles.contentRow,
              { flexDirection: isRTL ? 'row' : 'row-reverse' },
            ]}
          >
            {/* Bar chart */}
            <View style={styles.chartContainer}>
              <BarChart segments={stat.segments} isRTL={isRTL} />
            </View>

            {/* Total box -- only when showTotal is true */}
            {stat.showTotal && (
              <View style={styles.totalBox}>
                <Text style={styles.totalLabel}>
                  {t('talentsPage.totalParticipants')}
                </Text>
                <Text style={styles.totalValue}>
                  {stat.total}
                </Text>
              </View>
            )}
          </View>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    gap: 20,
  },
  rowMobile: {
    gap: 12,
  },
  card: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 20,
    gap: 20,
  },
  cardMobile: {
    flex: undefined,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  contentRow: {
    alignItems: 'stretch',
    gap: 16,
  },
  chartContainer: {
    flex: 1,
  },
  totalBox: {
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    padding: 8,
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 120,
  },
  totalLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    color: '#414042',
    lineHeight: 20,
  },
  totalValue: {
    fontSize: 24,
    fontFamily: FONTS.numbers,
    textAlign: 'center',
    color: '#414042',
  },
});

const barStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
  yColumn: {
    width: 28,
  },
  yChartPart: {
    height: BAR_CHART_HEIGHT,
    position: 'relative',
  },
  yLabel: {
    position: 'absolute',
    right: 4,
    fontSize: 10,
    fontFamily: FONTS.regular,
    lineHeight: 14,
    textAlign: 'right',
    color: '#737373',
    transform: [{ translateY: 7 }],
  },
  yLabelSpacer: {
    height: 34,
  },
  barsArea: {
    flex: 1,
    position: 'relative',
  },
  chartBorder: {
    height: BAR_CHART_HEIGHT,
    position: 'relative',
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomColor: '#d4d4d4',
    borderLeftColor: '#d4d4d4',
    borderRightColor: '#d4d4d4',
  },
  gridLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#d4d4d4',
  },
  columnsRow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'space-evenly',
    paddingHorizontal: 8,
    gap: 8,
  },
  column: {
    flex: 1,
    alignItems: 'center',
  },
  barTrack: {
    width: 40,
    height: BAR_CHART_HEIGHT,
    justifyContent: 'flex-end',
  },
  bar: {
    width: '100%',
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
    minHeight: 2,
  },
  barLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    lineHeight: 16,
    textAlign: 'center',
    marginTop: 8,
    color: '#64748b',
  },
});
